import { useState, useEffect } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import apiClient from "@/utils/apiClient";
import { TreePine, CheckCircle, Circle, Trophy, Target, Sparkles, Leaf, Sun, Snowflake } from "lucide-react";
import { Button } from "@/components/ui/button";

const SEASON_THEMES = {
  season_1: { icon: Leaf, label: "Foundation & Assessment", months: "January – April", color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", accent: "bg-emerald-500" },
  season_2: { icon: Sun, label: "Active Growth", months: "May – August", color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200", accent: "bg-amber-500" },
  season_3: { icon: Snowflake, label: "Maintenance & Reflection", months: "September – December", color: "text-violet-600", bg: "bg-violet-50", border: "border-violet-200", accent: "bg-violet-500" }
};

export default function Retreat() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [completing, setCompleting] = useState(null);
  const [expandedSeason, setExpandedSeason] = useState("season_1");

  const fetchProgram = async () => {
    try {
      const res = await apiClient.get('/retreat/program');
      setData(res.data);
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchProgram(); }, []);

  const handleEnroll = async () => {
    setEnrolling(true);
    try {
      await apiClient.post('/retreat/enroll');
      fetchProgram();
    } catch (err) { /* silent */ }
    finally { setEnrolling(false); }
  };

  const handleUnenroll = async () => {
    if (!window.confirm("Are you sure you want to leave the retreat program?")) return;
    try {
      await apiClient.post('/retreat/unenroll');
      fetchProgram();
    } catch (err) { /* silent */ }
  };

  const completeModule = async (moduleId) => {
    setCompleting(moduleId);
    try {
      await apiClient.post('/retreat/progress', { module_id: moduleId });
      fetchProgram();
    } catch (err) { /* silent */ }
    finally { setCompleting(null); }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-spa-terracotta border-t-transparent rounded-full animate-spin" /></div>
      </DashboardLayout>
    );
  }

  const enrollment = data?.enrollment;
  const isEnrolled = data?.is_enrolled;
  const seasons = data?.seasons || {};

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-5xl mx-auto" data-testid="retreat-page">
        <div className="mb-8">
          <p className="overline-text mb-2">Annual Program</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-medium text-spa-moss mb-2">ME.WELL Mind-SPA Retreat</h1>
          <p className="text-spa-text-secondary">A year-long Behavioural Change After-care Continuum of Care program</p>
        </div>

        {/* Enrollment CTA or Progress */}
        {!isEnrolled ? (
          <div className="spa-card bg-gradient-to-br from-white to-spa-sage/10 mb-8" data-testid="retreat-enroll-card">
            <div className="text-center max-w-xl mx-auto">
              <TreePine className="w-12 h-12 text-spa-sage mx-auto mb-4" />
              <h2 className="font-serif text-2xl text-spa-moss mb-3">Begin Your Wellness Journey</h2>
              <p className="text-spa-text-secondary mb-2">Three seasons of guided growth, self-discovery, and lasting behavioural change.</p>
              <div className="grid grid-cols-3 gap-3 my-6">
                {Object.entries(SEASON_THEMES).map(([key, s]) => (
                  <div key={key} className={`p-3 rounded-xl ${s.bg} ${s.border} border`}>
                    <s.icon className={`w-6 h-6 ${s.color} mx-auto mb-1`} />
                    <p className={`text-xs font-medium ${s.color}`}>{s.label}</p>
                    <p className="text-xs text-spa-text-secondary">{s.months}</p>
                  </div>
                ))}
              </div>
              <Button onClick={handleEnroll} disabled={enrolling} className="spa-button-primary" data-testid="enroll-btn">
                {enrolling ? "Enrolling..." : "Enroll Now — Free"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="spa-card mb-8" data-testid="retreat-progress-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-serif text-xl text-spa-moss">Your Journey</h2>
                <p className="text-spa-text-secondary text-sm">Currently in {SEASON_THEMES[enrollment.current_season]?.label}</p>
              </div>
              <button onClick={handleUnenroll} className="text-red-400 text-xs hover:underline" data-testid="unenroll-btn">Leave Program</button>
            </div>
            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-spa-text-secondary text-sm">Overall Progress</span>
                <span className="text-spa-moss font-medium">{enrollment.progress_pct || 0}%</span>
              </div>
              <div className="w-full h-3 bg-spa-muted rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-spa-sage to-spa-terracotta rounded-full transition-all duration-500" style={{ width: `${enrollment.progress_pct || 0}%` }} />
              </div>
            </div>
            {/* Milestones */}
            {enrollment.milestones_achieved?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {enrollment.milestones_achieved.map((m, i) => (
                  <span key={`milestone-${m}`} className="flex items-center gap-1 px-3 py-1 bg-spa-terracotta/10 text-spa-terracotta rounded-full text-xs">
                    <Trophy className="w-3 h-3" /> {m}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Seasons */}
        <div className="space-y-6">
          {Object.entries(SEASON_THEMES).map(([seasonKey, theme]) => {
            const seasonData = seasons[seasonKey];
            if (!seasonData) return null;
            const isExpanded = expandedSeason === seasonKey;
            const completedModules = enrollment?.completed_modules || [];

            return (
              <div key={seasonKey} className={`spa-card border ${theme.border} overflow-hidden`} data-testid={`season-${seasonKey}`}>
                <button onClick={() => setExpandedSeason(isExpanded ? "" : seasonKey)} className="w-full flex items-center justify-between p-0" data-testid={`toggle-${seasonKey}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl ${theme.bg} flex items-center justify-center`}>
                      <theme.icon className={`w-5 h-5 ${theme.color}`} />
                    </div>
                    <div className="text-left">
                      <h3 className="font-serif text-lg font-medium text-spa-moss">{theme.label}</h3>
                      <p className="text-spa-text-secondary text-xs">{theme.months} — {seasonData.modules?.length || 0} modules</p>
                    </div>
                  </div>
                  <span className={`text-spa-text-secondary text-sm transform transition-transform ${isExpanded ? "rotate-180" : ""}`}>▾</span>
                </button>

                {isExpanded && seasonData.modules?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-spa-border space-y-3">
                    {seasonData.modules.map(m => {
                      const isCompleted = completedModules.includes(m.module_id);
                      return (
                        <div key={m.module_id} className={`p-4 rounded-xl border transition-all ${isCompleted ? "bg-spa-sage/5 border-spa-sage/20" : "bg-white border-spa-border hover:border-spa-sage/40"}`} data-testid={`module-${m.module_id}`}>
                          <div className="flex items-start gap-3">
                            <div className="mt-0.5">
                              {isCompleted ? (
                                <CheckCircle className="w-5 h-5 text-spa-sage" />
                              ) : (
                                <Circle className="w-5 h-5 text-spa-border" />
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-spa-text-secondary text-xs">Week {m.week_number}</span>
                              </div>
                              <h4 className={`font-medium mb-1 ${isCompleted ? "text-spa-sage" : "text-spa-moss"}`}>{m.title}</h4>
                              <p className="text-spa-text-secondary text-sm mb-2">{m.description}</p>
                              {m.goals?.length > 0 && (
                                <div className="mb-2">
                                  <p className="text-xs font-medium text-spa-moss mb-1 flex items-center gap-1"><Target className="w-3 h-3" /> Goals</p>
                                  <ul className="text-xs text-spa-text-secondary space-y-0.5 ml-4">{m.goals.map((g, i) => <li key={`goal-${g}-${i}`}>• {g}</li>)}</ul>
                                </div>
                              )}
                              {m.activities?.length > 0 && (
                                <div className="mb-2">
                                  <p className="text-xs font-medium text-spa-moss mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Activities</p>
                                  <ul className="text-xs text-spa-text-secondary space-y-0.5 ml-4">{m.activities.map((a, i) => <li key={`activity-${a}-${i}`}>• {a}</li>)}</ul>
                                </div>
                              )}
                              {isEnrolled && !isCompleted && (
                                <Button onClick={() => completeModule(m.module_id)} disabled={completing === m.module_id} variant="outline" size="sm" className="mt-2 rounded-full border-spa-sage text-spa-sage hover:bg-spa-sage/10" data-testid={`complete-${m.module_id}`}>
                                  {completing === m.module_id ? "Completing..." : "Mark Complete"}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {isExpanded && (!seasonData.modules || seasonData.modules.length === 0) && (
                  <div className="mt-4 pt-4 border-t border-spa-border text-center py-6">
                    <p className="text-spa-text-secondary text-sm">Modules coming soon for this season</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
}
